import Tactic from '@/models/Tactic'
import axios from 'axios'
import TacticFieldValue from '@/models/TacticFieldValue'
// import TacticsImportMapping from '@/models/TacticsImportMapping' // Archiving TacticImport.vue - JV 04.18.23
import MediaAsset from '@/models/MediaAsset'
import ID from '@/models/ID'
import { getApiRangeEndBound, getApiRangeStartBound } from '@/utils/apiDateBounds'

const TACTICS_PATH = 'tactics'
// const TACTIC_PATH = 'tactic' // Archiving TacticImport.vue - JV 04.18.23
const TACTIC_FIELD_VALUES_PATH = 'tactic_field_values'
const MEDIA_ASSETS_PATH = 'media_assets'
const TACTIC_SUMMARIES_PATH = 'tactic_summaries'

export default class TacticsService {
  public create(newTactic: Tactic) {
    return new Promise((resolve, reject) => {
      axios
        .post(TACTICS_PATH, { ...newTactic.forRequestObject(), id: null })
        .then(
          (response) => {
            resolve(Tactic.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public get(ids: number[]) {
    return new Promise((resolve, reject) => {
      axios.get(`${TACTICS_PATH}/${ids.toString()}`).then(
        (response) => {
          ids.length !== 0
            ? resolve(Tactic.fromResponseObject(response.data))
            : resolve(
              response.data.map((tactic) => Tactic.fromResponseObject(tactic))
            )
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public fetchTacticsForPlanInDateRange(planIds: number[], dateRange: Date[], shouldFetchRecurringTactics: boolean = false) {
    // ID options:
      // Single: plan.id=123
      // Array: plan.id[]=123&plan.id[]=124

    // Date filter options:
      // startDate[after]=
      // startDate[before]=
      // startDate[strictly_after]= // Excludes tactics that start on the specified date
      // startDate[strictly_before]= // Excludes tactics that end on the specified date
      // endDate[after]=
      // endDate[before]=
      // endDate[strictly_after]= // Excludes tactics that start on the specified date
      // endDate[strictly_before]= // Excludes tactics that end on the specified date

    // Unscheduled flag
      // /tactics?isUnscheduled=true|false

    // Example: /api/tactics?plan.id[]=86&plan.id[]=87&plan.id[]=88&startDate[after]=2025-01-01&endDate[before]=2025-12-31
    
    let requestString = ''
    if(planIds.length === 1){
      requestString = `${TACTICS_PATH}?plan.id=${planIds.toString()}`
    }
    if (planIds.length > 1) {
      requestString = `${TACTICS_PATH}?plan.id[]=${planIds.join('&plan.id[]=')}`
    }
    if(shouldFetchRecurringTactics){
      requestString += `&exists[recurrence]=true`
    }else{
      requestString += `&startDate[before]=${getApiRangeEndBound(dateRange[1])}&endDate[after]=${getApiRangeStartBound(dateRange[0])}&exists[recurrence]=false`
    }
    requestString += `&isUnscheduled=false`
    

    return new Promise((resolve, reject) => {
      axios.get(requestString).then(
        (response) => {
          resolve(response.data['hydra:member'].map((tactic) => Tactic.fromResponseObject(tactic)))
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public fetchUnscheduledTacticsForPlanIds(planIds: number[],) {
    // ID options:
      // Single: plan.id=123
      // Array: plan.id[]=123&plan.id[]=124
      
    // Unscheduled flag
      // /tactics?isUnscheduled=true|false

    let requestString = ''
    if(planIds.length === 1){
      requestString = `${TACTICS_PATH}?plan.id=${planIds.toString()}`
    }
    if (planIds.length > 1) {
      requestString = `${TACTICS_PATH}?plan.id[]=${planIds.join('&plan.id[]=')}`
    }
    requestString += `&isUnscheduled=true`
    

    return new Promise((resolve, reject) => {
      axios.get(requestString).then(
        (response) => {
          resolve(response.data['hydra:member'].map((tactic) => Tactic.fromResponseObject(tactic)))
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public fetchTacticsRelatedToInitiative(initiativeId: number) {
    return new Promise((resolve, reject) => {
      axios.get(`${TACTICS_PATH}?initiatives.id[]=${initiativeId}`).then(
        (response) => {
          resolve(response.data['hydra:member'].map((tactic) => Tactic.fromResponseObject(tactic)))
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public update(updatedTactic: Tactic, changedFields?: string[]) {
    return new Promise((resolve, reject) => {
      axios
        .put(`${TACTICS_PATH}/${updatedTactic.id.intID}`, {
          ...updatedTactic.forRequestObject(),
        })
        .then(
          (response) => {
            resolve(Tactic.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public delete(id: number) {
    return new Promise((resolve, reject) => {
      axios.delete(`${TACTICS_PATH}/${id}`).then(
        (response) => {
          resolve(response.data)
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  // Archiving TacticImport.vue - JV 04.18.23
  // public import(fileBinary: string) {
  //   return new Promise((resolve, reject) => {
  //     axios.post(`${TACTIC_PATH}/import}`, fileBinary).then(
  //       (response) => {
  //         resolve(
  //           response.data.map((tactic) => Tactic.fromResponseObject(tactic))
  //         )
  //       },
  //       (error) => {
  //         reject(error)
  //       }
  //     )
  //   })
  // }

  public search(
    planId: number,
    keyword: string,
    startDate?: Date,
    endDate?: Date,
    shouldFetchRecurringTactics?: boolean,
  ) {
    return new Promise((resolve, reject) => {
      let requestString = `${TACTICS_PATH}?plan.id[]=${planId}&title=${keyword}`
      if(shouldFetchRecurringTactics){
        requestString += `&exists[recurrence]=true`
      }else{
        requestString += `&exists[recurrence]=false`

        if(endDate){
          requestString += `&startDate[before]=${getApiRangeEndBound(endDate)}`
        }
        if(startDate){
          requestString += `&endDate[after]=${getApiRangeStartBound(startDate)}`
        }
      }

      axios
        .get(requestString)
        .then(
          (response) => {
            resolve(response.data['hydra:member'].map((tactic) => Tactic.fromResponseObject(tactic)))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public createTacticFieldValue(newFieldValue: TacticFieldValue) {
    return new Promise((resolve, reject) => {
      axios
        .post(TACTIC_FIELD_VALUES_PATH, {
          ...newFieldValue.forRequestObject(),
          id: null,
        })
        .then(
          (response) => {
            resolve(TacticFieldValue.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public updateTacticFieldValue(
    updatedFieldValue: TacticFieldValue,
    changedFields?: string[]
  ) {
    return new Promise((resolve, reject) => {
      axios
        .put(`${TACTIC_FIELD_VALUES_PATH}/${updatedFieldValue.id.intID}`, {
          ...updatedFieldValue.forRequestObject(),
        })
        .then(
          (response) => {
            resolve(TacticFieldValue.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public deleteTacticFieldValue(ids: number[]) {
    return new Promise((resolve, reject) => {
      axios.delete(`${TACTIC_FIELD_VALUES_PATH}/${ids.toString()}`).then(
        (response) => {
          resolve(response.data)
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  // Archiving TacticImport.vue - JV 04.18.23
  // public importTacticsFile(tacticTypeIntId: number, fileToImport) {
  //   return new Promise((resolve, reject) => {
  //     axios
  //       .post(`${TACTICS_PATH}/import/${tacticTypeIntId.toString()}`, fileToImport)
  //       .then(
  //         (response) => {
  //           resolve(TacticsImportMapping.fromResponseObject(response.data))
  //         },
  //         (error) => {
  //           reject(error)
  //         }
  //       )
  //   })
  // }

  public importCustomFieldImage(tacticId: number, tacticTypeIntId: number, fileToImport) {
    return new Promise((resolve, reject) => {
      axios.post(`${MEDIA_ASSETS_PATH}`, fileToImport).then(
        (response) => {
          resolve(MediaAsset.fromResponseObject(response.data))
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public getMediaAsset(id: number) {
    return axios
      .get(`${MEDIA_ASSETS_PATH}/${id}`)
      .then((response) => MediaAsset.fromResponseObject(response.data))
  }

  // Archiving TacticImport.vue - JV 04.18.23
  // public mapTacticsImport(tacticImportMapping: TacticsImportMapping) {
  //   return new Promise((resolve, reject) => {
  //     axios
  //       .post(
  //         `${TACTICS_PATH}/import-column-definitions`,
  //         tacticImportMapping.forRequestObject()
  //       )
  //       .then(
  //         (response) => {
  //           resolve(response.data)
  //         },
  //         (error) => {
  //           reject(error)
  //         }
  //       )
  //   })
  // }

  // Archiving TacticImport.vue - JV 04.18.23
  // public confirmTacticsImport(tacticsImportMapping: TacticsImportMapping, planId: ID, rowIndiciesToCreate: number[]) {
  //   return new Promise((resolve, reject) => {
  //     axios
  //       .post(`${TACTICS_PATH}/import-confirm`, {
  //         id: tacticsImportMapping.id,
  //         confirm: true,
  //         planId: planId.apiID,
  //         rows: rowIndiciesToCreate,
  //       })
  //       .then(
  //         (response) => {
  //           resolve(response.data)
  //         },
  //         (error) => {
  //           reject(error)
  //         }
  //       )
  //   })
  // }

  public async fetchSummaryContent(contentUrl: string): Promise<string> {
    const client = axios.create()
    delete client.defaults.headers.common['Authorization']
    const response = await client.get(contentUrl)
    return response.data
  }

  public createTacticSummary(tacticIRI: string, content: string) {
    return new Promise((resolve, reject) => {
      axios.post(TACTIC_SUMMARIES_PATH, { tactic: tacticIRI, content }).then(
        (response) => {
          resolve({ id: ID.fromResponseObject(response.data['@id'], 'tactic_summaries'), contentUrl: response.data.contentUrl })
        },
        (error) => { reject(error) }
      )
    })
  }

  public updateTacticSummary(summaryId: number, content: string) {
    return new Promise((resolve, reject) => {
      axios.put(`${TACTIC_SUMMARIES_PATH}/${summaryId}`, { content }).then(
        (response) => {
          resolve({ id: ID.fromResponseObject(response.data['@id'], 'tactic_summaries'), contentUrl: response.data.contentUrl })
        },
        (error) => { reject(error) }
      )
    })
  }
}
